export type Locale = 'fr' | 'en';

export interface Translations {
  auth: {
    login: {
      title: string;
      subtitle: string;
      email: string;
      emailPlaceholder: string;
      password: string;
      passwordPlaceholder: string;
      forgotPassword: string;
      submitButton: string;
      contactSupport: string;
      noAccount: string;
      heroTitle: string;
      heroSubtitle: string;
    };
    forgotPassword: {
      title: string;
      subtitle: string;
      email: string;
      emailPlaceholder: string;
      submitButton: string;
      backToLogin: string;
      successMessage: string;
    };
    selectAccount: {
      title: string;
      subtitle: string;
      noAccounts: string;
      /** Header shown when the session already exists (single-merchant login). */
      confirmTitle: string;
      confirmSubtitle: string;
      /** Why non-active rows are inert while the re-auth dialog is pending (D4). */
      switchDisabledHint: string;
      /** Fallback when `/account/session` fails and the backend sent no usable message. */
      sessionFailed: string;
      /** Fallback when the session opened but loading the merchant's details failed. */
      detailsFailed: string;
      /**
       * Code 321 on this screen: the backend lists accounts with `status != 0` but only opens a
       * session on `status == 2`, so a listed account can still be rejected. Scoped to that row —
       * the other accounts remain usable.
       */
      accountUnavailable: string;
      /** Every listed account came back unavailable — nothing left to pick. */
      noneAvailable: string;
      /** Escape hatch off the picker; the only one, since choosing is otherwise mandatory. */
      backToLogin: string;
    };
    errors: {
      required: string;
      invalidEmail: string;
      minLength: string;
      loginFailed: string;
      emailNotFound: string;
      invalidPassword: string;
      /** Singular form, interpolates {{count}}. */
      attemptsRemainingOne: string;
      /** Plural form, interpolates {{count}}. */
      attemptsRemainingMany: string;
      networkError: string;
      formInvalid: string;
      accountLocked: string;
    };
  };
  common: {
    loading: string;
    language: string;
  };
  settings: {
    recoverPin: {
      entry: { title: string; subtitle: string };
      steps: {
        password: string;
        verification: string;
        newCode: string;
        /** Accessible name for the progress strip itself. */
        progressLabel: string;
      };
      titles: {
        root: string;
        password: string;
        channel: string;
        otp: string;
        newPin: string;
        success: string;
        discard: string;
      };
      password: {
        lead: string;
        label: string;
        placeholder: string;
        cta: string;
        ctaLoading: string;
        wrong: string;
        /** Singular form, interpolates {{count}}. */
        wrongRemainingOne: string;
        /** Plural form, interpolates {{count}}. */
        wrongRemainingMany: string;
        /** Accessible name for the reveal toggle, in each state. */
        show: string;
        hide: string;
      };
      channel: {
        lead: string;
        groupLabel: string;
        cta: string;
        ctaSending: string;
        sms: string;
        whatsapp: string;
        email: string;
        none: string;
        sendFailed: string;
      };
      otp: {
        /** Interpolates {{channel}} and {{expiry}}. */
        lead: string;
        bySms: string;
        byWhatsapp: string;
        byEmail: string;
        cta: string;
        resend: string;
        /** Interpolates {{time}} as mm:ss. */
        resendIn: string;
        resent: string;
        invalid: string;
        resendFailed: string;
        defaultExpiry: string;
      };
      pin: { lead: string; cta: string; ctaSaving: string; encryptionFailed: string };
      success: { title: string; description: string; cta: string };
      blocked: { title: string; description: string; cta: string };
      network: { title: string; description: string; retry: string; close: string };
      session: { title: string; description: string; cta: string };
      discard: { title: string; description: string; confirm: string; cancel: string };
      /** Interpolates {{index}} and {{name}}. */
      stepAnnouncement: string;
      close: string;
      genericError: string;
    };
  };
  landing: {
    nav: {
      partners: string;
      benefits: string;
      testimonials: string;
      contact: string;
      login: string;
      myAccount: string;
    };
    hero: {
      badge: string;
      title: string;
      subtitle: string;
      ctaPrimary: string;
      ctaPartner: string;
      ctaSecondary: string;
      statValue: string;
      statLabel: string;
      stats: LandingStat[];
    };
    joinForm: {
      title: string;
      subtitle: string;
      partnerTypeLabel: string;
      partnerTypes: string[];
      /** Short description shown under the selected partner type (index-aligned with partnerTypes). */
      partnerTypeDescriptions: string[];
      companyNamePlaceholder: string;
      firstNamePlaceholder: string;
      lastNamePlaceholder: string;
      emailPlaceholder: string;
      agentNamePlaceholder: string;
      agentCodePlaceholder: string;
      requiredError: string;
      emailError: string;
      phoneLabel: string;
      phonePlaceholder: string;
      consent: string;
      howLabel: string;
      howOptions: string[];
      submit: string;
      success: {
        title: string;
        body: string;
        close: string;
      };
      /** Toast messages for backend error codes on lead submission. */
      errors: {
        title: string;
        fillAllFields: string;
        phoneAlreadyRegistered: string;
        emailAlreadyRegistered: string;
        generic: string;
      };
    };
    marquee: {
      label: string;
    };
    partners: {
      eyebrow: string;
      title: string;
      subtitle: string;
      reasonsLabel: string;
      benefitsLabel: string;
      /** Shared secondary CTA on every partner panel. */
      advisorCta: string;
      types: LandingPartnerType[];
    };
    coworking: {
      eyebrow: string;
      title: string;
      lead: string;
      location: string;
      /** Short label for the footer nav entry. */
      navLabel: string;
      ctaVisit: string;
      ctaSpaces: string;
      amenities: LandingCoworkingAmenity[];
      spaces: LandingCoworkingSpace[];
    };
    testimonials: {
      eyebrow: string;
      title: string;
      subtitle: string;
      readMore: string;
      readLess: string;
      items: LandingTestimonial[];
    };
    contact: {
      title: string;
      subtitle: string;
      cta: string;
    };
    footer: {
      tagline: string;
      rights: string;
    };
  };
}

export interface LandingPartnerType {
  id: string;
  label: string;
  tag: string;
  icon: string;
  headline: string;
  intro: string;
  reasons: LandingReason[];
  benefits: LandingBenefit[];
}

/**
 * A coworking space shown in the mosaic. `id` selects the bundled photo (see
 * SPACE_IMAGES in landing-coworking.ts) and must stay identical across locales.
 */
export interface LandingCoworkingSpace {
  id: string;
  icon: string;
  title: string;
  meta: string;
}

export interface LandingCoworkingAmenity {
  icon: string;
  label: string;
}

export interface LandingReason {
  icon: string;
  text: string;
}

export interface LandingBenefit {
  title: string;
  description: string;
}

export interface LandingStat {
  value: string;
  label: string;
}

export interface LandingTestimonial {
  quote: string;
  author: string;
  role: string;
}

export const translations: Record<Locale, Translations> = {
  fr: {
    auth: {
      login: {
        title: 'Bienvenue !',
        subtitle: 'Connectez-vous à votre compte',
        email: 'Email',
        emailPlaceholder: 'Entrer adresse email',
        password: 'Password',
        passwordPlaceholder: 'Mot de passe',
        forgotPassword: 'Mot de passe oublié?',
        submitButton: 'Connexion',
        contactSupport: 'Contacter le support',
        noAccount: 'Pas de compte ?',
        heroTitle: 'Faites avancer votre business en toute simplicité avec Gozem',
        heroSubtitle:
          'Gozem Corporate vous offre un environnement digital complet pour automatiser vos flux et améliorer vos performances',
      },
      forgotPassword: {
        title: "Vérification d'adresse email",
        subtitle: 'Renseignez votre adresse email pour continuer',
        email: 'Email',
        emailPlaceholder: 'Entrer adresse email',
        submitButton: 'Continuer',
        backToLogin: 'Retour à la connexion',
        successMessage:
          'Un email de réinitialisation a été envoyé. Vérifiez votre boîte de réception.',
      },
      selectAccount: {
        title: 'Sélectionnez un compte',
        subtitle: 'Sur quel compte souhaitez-vous vous connecter ?',
        noAccounts: 'Aucun compte disponible',
        confirmTitle: 'Confirmez votre compte',
        confirmSubtitle: 'Continuez sur le compte ci-dessous.',
        switchDisabledHint:
          'Pour changer de compte, utilisez le sélecteur de compte une fois connecté.',
        sessionFailed: 'Impossible de vous connecter à ce compte',
        detailsFailed: 'Impossible de charger les détails du compte',
        accountUnavailable: "Ce compte n'est plus disponible. Choisissez-en un autre.",
        noneAvailable: "Aucun de vos comptes n'est disponible. Contactez le support.",
        backToLogin: 'Retour à la connexion',
      },
      errors: {
        required: 'Ce champ est obligatoire',
        invalidEmail: 'Adresse email invalide',
        minLength: 'Minimum {{min}} caractères requis',
        loginFailed: 'Email ou mot de passe incorrect',
        emailNotFound: "Cet email n'est pas enregistré",
        invalidPassword: 'Mot de passe incorrect',
        attemptsRemainingOne: 'Il vous reste {{count}} tentative.',
        attemptsRemainingMany: 'Il vous reste {{count}} tentatives.',
        networkError: 'Erreur de connexion. Veuillez réessayer.',
        formInvalid: 'Entrez vos identifiants pour continuer',
        accountLocked: 'Compte verouillé suite à trop de tentatives échouées',
      },
    },
    common: {
      loading: 'Chargement...',
      language: 'Fr',
    },
    settings: {
      recoverPin: {
        entry: {
          title: 'Récupérer mon code de sécurité',
          subtitle: 'Réinitialisez le code PIN utilisé pour vos transactions',
        },
        steps: {
          password: 'Mot de passe',
          verification: 'Vérification',
          newCode: 'Nouveau code',
          progressLabel: 'Progression',
        },
        titles: {
          root: 'Récupérer mon code de sécurité',
          password: 'Entrez votre mot de passe',
          channel: 'Comment recevoir votre code\u202F?',
          otp: 'Saisissez le code reçu',
          newPin: 'Choisissez un nouveau code',
          success: 'Terminé',
          discard: 'Abandonner\u202F?',
        },
        password: {
          lead: 'Pour votre sécurité, saisissez le mot de passe de votre compte avant de réinitialiser votre code.',
          label: 'Mot de passe',
          placeholder: 'Votre mot de passe',
          cta: 'Continuer',
          ctaLoading: 'Vérification…',
          wrong: 'Mot de passe incorrect. Vérifiez votre saisie et réessayez.',
          wrongRemainingOne: 'Mot de passe incorrect. Il vous reste {{count}} tentative.',
          wrongRemainingMany: 'Mot de passe incorrect. Il vous reste {{count}} tentatives.',
          show: 'Afficher le mot de passe',
          hide: 'Masquer le mot de passe',
        },
        channel: {
          lead: "Nous vous enverrons un code à 4 chiffres pour confirmer que c'est bien vous.",
          groupLabel: 'Méthode de vérification',
          cta: 'Envoyer le code',
          ctaSending: 'Envoi…',
          sms: 'SMS',
          whatsapp: 'WhatsApp',
          email: 'E-mail',
          none: 'Aucune méthode de vérification n’est disponible sur votre compte. Contactez le support.',
          sendFailed: 'Le code n’a pas pu être envoyé. Vérifiez votre connexion et réessayez.',
        },
        otp: {
          lead: 'Code envoyé {{channel}}. Il reste valable {{expiry}}.',
          bySms: 'par SMS',
          byWhatsapp: 'via WhatsApp',
          byEmail: 'par e-mail',
          cta: 'Continuer',
          resend: 'Renvoyer le code',
          resendIn: 'Renvoyer le code dans {{time}}',
          resent: 'Un nouveau code vient de vous être envoyé.',
          invalid:
            'Ce code n’est pas valide ou a expiré. Saisissez-le à nouveau, ou demandez un nouveau code.',
          resendFailed: 'Impossible de renvoyer le code. Vérifiez votre connexion et réessayez.',
          defaultExpiry: '30 minutes',
        },
        pin: {
          lead: 'Ce code à 4 chiffres protégera vos transactions.',
          cta: 'Enregistrer le code',
          ctaSaving: 'Enregistrement…',
          encryptionFailed: 'Impossible de sécuriser votre code. Réessayez dans un instant.',
        },
        success: {
          title: 'Code de sécurité mis à jour',
          description: 'Utilisez votre nouveau code pour valider vos prochaines transactions.',
          cta: 'Terminer',
        },
        blocked: {
          title: 'Trop de tentatives',
          description:
            'Pour protéger votre compte, la réinitialisation est temporairement bloquée. Réessayez plus tard ou contactez le support.',
          cta: 'Fermer',
        },
        network: {
          title: 'Connexion interrompue',
          description:
            "Votre code n'a pas été modifié. Vérifiez votre connexion et réessayez — votre code actuel reste valable.",
          retry: 'Réessayer',
          close: 'Fermer',
        },
        session: {
          title: 'Session expirée',
          description: 'Reconnectez-vous pour réinitialiser votre code de sécurité.',
          cta: 'Fermer',
        },
        discard: {
          title: 'Abandonner la réinitialisation\u202F?',
          description:
            'Le code de vérification déjà envoyé sera annulé et vous devrez tout recommencer.',
          confirm: 'Abandonner',
          cancel: 'Continuer la réinitialisation',
        },
        stepAnnouncement: 'Étape {{index}} sur 3 : {{name}}',
        close: 'Fermer',
        genericError: 'Une erreur est survenue. Veuillez réessayer.',
      },
    },
    landing: {
      nav: {
        partners: 'Partenaires',
        benefits: 'Avantages',
        testimonials: 'Témoignages',
        contact: 'Contact',
        login: 'Se connecter',
        myAccount: 'Mon compte',
      },
      hero: {
        badge: 'La Super App N°1 en Afrique',
        title: 'Devenez un Partenaire Gozem',
        subtitle: 'Rejoignez la Super App N°1 en Afrique comme 12 000 autres partenaires.',
        ctaPrimary: 'Rejoindre Gozem',
        ctaPartner: 'Devenir Partenaire',
        ctaSecondary: 'Voir les témoignages',
        statValue: '12 000+',
        statLabel: 'partenaires nous font confiance',
        stats: [
          { value: '12 000+', label: 'partenaires' },
          { value: '5', label: 'pays' },
          { value: '5', label: 'profils partenaires' },
        ],
      },
      joinForm: {
        title: 'Rejoignez-nous',
        subtitle: 'Devenez partenaire en quelques étapes.',
        partnerTypeLabel: 'Type de partenaire',
        partnerTypes: ['Commerce en ligne', 'Mobilité Pro', 'Coursier', 'Flotte', 'Freelance'],
        partnerTypeDescriptions: [
          "Recevez vos commandes sur l'application Marchand, nos livreurs s'occupent du reste.",
          'Un compte entreprise pour commander et payer les trajets de vos collaborateurs.',
          'Faites livrer vos plis et colis dans la journée, suivis en temps réel.',
          'Mettez vos véhicules en service avec des chauffeurs déjà formés par Gozem.',
          "Recommandez Gozem et touchez une commission dès la première activité de vos filleuls.",
        ],
        companyNamePlaceholder: "Nom de l'entreprise",
        firstNamePlaceholder: 'Prénom',
        lastNamePlaceholder: 'Nom',
        emailPlaceholder: 'Adresse e-mail',
        agentNamePlaceholder: "Nom de l'agent",
        agentCodePlaceholder: 'Code de parrainage agent',
        requiredError: 'Ce champ est requis.',
        emailError: 'Veuillez saisir une adresse e-mail valide.',
        phoneLabel: 'Numéro de téléphone',
        phonePlaceholder: 'Numéro de téléphone',
        consent: "J'accepte de recevoir des messages sur WhatsApp / par SMS",
        howLabel: 'Comment vous nous avez connu ?',
        howOptions: [
          'Agent Gozem',
          'Facebook',
          'Instagram',
          'Google',
          'Twitter',
          'Linkedin',
          'Tik Tok',
          'SMS',
          'Courriel',
          'Panneau Publicitaire',
          'TV',
          'Radio',
          'Notification',
          'Site web',
          'Autre',
        ],
        submit: 'Envoyer',
        success: {
          title: 'Inscription complète',
          body: 'Bienvenue à Gozem ! Nous allons maintenant traiter votre demande. Nous vous informerons dès que nous aurons terminé de traiter votre demande.',
          close: 'Fermer',
        },
        errors: {
          title: 'Erreur',
          fillAllFields: 'Veuillez remplir tous les champs.',
          phoneAlreadyRegistered: 'Ce numéro de téléphone est déjà enregistré.',
          emailAlreadyRegistered: 'Cette adresse e-mail est déjà enregistrée.',
          generic: 'Une erreur est survenue. Veuillez réessayer.',
        },
      },
      marquee: {
        label: "La confiance de partenaires en Afrique de l'Ouest et centrale",
      },
      partners: {
        eyebrow: 'Cinq façons de collaborer',
        title: 'Boostez votre business avec Gozem',
        subtitle:
          'Cinq profils, une seule plateforme. Choisissez le vôtre et découvrez, concrètement, ce que Gozem change pour votre activité.',
        reasonsLabel: 'Est-ce vous ?',
        benefitsLabel: 'Ce que vous obtenez',
        advisorCta: 'Parler à un conseiller',
        types: [
          {
            id: 'merchant',
            label: 'Commerce en ligne',
            tag: 'Vendez plus',
            icon: 'ri-store-2-line',
            headline: 'Vendez plus, nous livrons pour vous',
            intro:
              "Inscrivez votre commerce sur Gozem, recevez les commandes depuis l'application Marchand et laissez notre flotte de livreurs s'occuper du reste.",
            reasons: [
              {
                icon: 'ri-restaurant-2-line',
                text: 'Vous tenez un restaurant ou un maquis avec une clientèle fidèle',
              },
              { icon: 'ri-shopping-bag-3-line', text: 'Vous vendez des produits locaux en ligne' },
              {
                icon: 'ri-megaphone-line',
                text: 'Vous voulez être connu au-delà de votre quartier',
              },
            ],
            benefits: [
              {
                title: 'La livraison au service de vos ventes',
                description:
                  "Recevez et traitez vos commandes depuis l'application Marchand. Nos livreurs prennent le relais, vous restez concentré sur votre production.",
              },
              {
                title: 'Un accompagnement continu',
                description:
                  'Un coach dédié suit vos performances et vous forme régulièrement : délais de préparation, qualité de service, notes clients.',
              },
              {
                title: 'Le marketing inclus',
                description:
                  "Participez aux campagnes promotionnelles Gozem et gagnez en visibilité dans l'application comme sur nos réseaux sociaux.",
              },
            ],
          },
          {
            id: 'mobility',
            label: 'Mobilité Pro',
            tag: 'Déplacez vos équipes',
            icon: 'ri-briefcase-4-line',
            headline: 'Maîtrisez les déplacements de vos équipes',
            intro:
              'Un compte entreprise unique pour commander, encadrer et payer tous les trajets de vos collaborateurs, sans avance de frais.',
            reasons: [
              {
                icon: 'ri-team-line',
                text: 'Vos équipes se déplacent chaque jour pour vos opérations',
              },
              {
                icon: 'ri-moon-line',
                text: 'Vos collaborateurs finissent tard ou commencent très tôt',
              },
              {
                icon: 'ri-group-line',
                text: "Vous n'avez pas de véhicules de fonction à mettre à disposition",
              },
            ],
            benefits: [
              {
                title: 'Des règles claires par équipe',
                description:
                  "Créez des groupes d'utilisateurs et fixez leurs règles d'utilisation : budgets, horaires, services autorisés. Plus de validation au cas par cas.",
              },
              {
                title: "Des rapports prêts à l'emploi",
                description:
                  'Suivez les dépenses par collaborateur, par département ou par période, et exportez vos rapports pour la comptabilité.',
              },
              {
                title: 'Un seul compte à régler',
                description:
                  'Tous les trajets sont payés depuis le compte entreprise, avec une facturation centralisée et traçable. Fini les notes de frais.',
              },
            ],
          },
          {
            id: 'courier',
            label: 'Coursier',
            tag: 'Livrez vos colis',
            icon: 'ri-e-bike-2-line',
            headline: 'Faites livrer vos colis dans la journée',
            intro:
              "Confiez vos plis et vos colis à des coursiers professionnels, suivis en temps réel du départ jusqu'à la remise.",
            reasons: [
              {
                icon: 'ri-mail-send-line',
                text: 'Vous envoyez régulièrement des documents à vos clients',
              },
              {
                icon: 'ri-shopping-cart-2-line',
                text: 'Vous tenez une boutique en ligne à livrer',
              },
              {
                icon: 'ri-restaurant-line',
                text: 'Vous êtes restaurateur et vous livrez vos plats',
              },
            ],
            benefits: [
              {
                title: 'Un coursier en trois clics',
                description:
                  'Commandez à la demande et regroupez plusieurs colis ou plusieurs arrêts dans une même course, depuis la même interface.',
              },
              {
                title: 'Des envois sécurisés par code',
                description:
                  "Générez un code PIN à la commande : le colis n'est remis qu'à la personne qui détient ce code. Vos envois sont couverts de bout en bout.",
              },
              {
                title: 'Vous choisissez qui paie',
                description:
                  "Faites régler la livraison par l'expéditeur ou par le destinataire, selon ce qui arrange votre activité.",
              },
            ],
          },
          {
            id: 'fleet',
            label: 'Flotte',
            tag: 'Rentabilisez vos véhicules',
            icon: 'ri-taxi-line',
            headline: 'Rentabilisez votre flotte, sans la gérer seul',
            intro:
              'Mettez vos véhicules en service sur Gozem avec des chauffeurs déjà formés et un suivi complet de votre activité.',
            reasons: [
              { icon: 'ri-car-line', text: 'Vous possédez une flotte de véhicules de location' },
              {
                icon: 'ri-roadster-line',
                text: 'Vous êtes un particulier avec plusieurs voitures ou motos',
              },
              { icon: 'ri-building-line', text: 'Vous cherchez un placement rentable et suivi' },
            ],
            benefits: [
              {
                title: 'Des chauffeurs déjà formés',
                description:
                  "Nous vous mettons en relation avec nos Champions : des chauffeurs partenaires sélectionnés, vérifiés et formés à l'application.",
              },
              {
                title: 'Votre activité en temps réel',
                description:
                  'Suivez la position et les performances de chaque Champion depuis votre plateforme. Les données sont brutes et exportables : vos analyses restent les vôtres.',
              },
              {
                title: 'Une trésorerie sous contrôle',
                description:
                  'Vos gains sont collectés sur votre portefeuille électronique et reversés par virement bancaire, au rythme que vous choisissez.',
              },
            ],
          },
          {
            id: 'freelance',
            label: 'Freelance',
            tag: 'Gagnez en recommandant',
            icon: 'ri-user-star-line',
            headline: "Gagnez de l'argent en parlant de Gozem",
            intro:
              "Faites découvrir Gozem autour de vous et touchez une commission dès la première activité de chacun de vos filleuls.",
            reasons: [
              {
                icon: 'ri-graduation-cap-line',
                text: "Vous êtes étudiant et à l'aise avec le digital",
              },
              { icon: 'ri-share-forward-line', text: 'Vous aimez partager vos bons plans' },
              {
                icon: 'ri-medal-line',
                text: 'Vous êtes jeune diplômé et cherchez un revenu complémentaire',
              },
            ],
            benefits: [
              {
                title: 'Recommandez, tout simplement',
                description:
                  'Installez Gozem à vos proches, vos voisins, vos collègues. Chaque inscription validée est enregistrée sur votre compte freelance.',
              },
              {
                title: "Des revenus à chaque nouveau filleul",
                description:
                  "Vous touchez une commission dès la première commande d'un de vos filleuls, qu'il s'agisse d'une course, d'un repas ou d'une livraison sur l'application.",
              },
              {
                title: 'Aucun matériel, aucun horaire',
                description:
                  "Un smartphone suffit. Vous travaillez d'où vous voulez, quand vous voulez, sans objectif imposé.",
              },
            ],
          },
        ],
      },
      coworking: {
        eyebrow: 'Nouveau au Togo',
        title: 'Gozem Coworking',
        lead: 'Un cadre moderne pour travailler, recevoir et avancer. À Lomé, Gozem Coworking réunit open space, bureaux privés, salle de réunion, espaces communs et rooftop.',
        location: 'Nyékonakpoé, Lomé — Direction Générale de Gozem Togo',
        navLabel: 'Coworking',
        ctaVisit: 'Demander une visite',
        ctaSpaces: 'Voir les espaces',
        amenities: [
          { icon: 'ri-wifi-line', label: 'Internet haut débit' },
          { icon: 'ri-restaurant-2-line', label: 'Cuisine équipée' },
          { icon: 'ri-sofa-line', label: 'Salle de détente' },
          { icon: 'ri-snowy-line', label: 'Climatisation' },
          { icon: 'ri-flashlight-line', label: 'Groupe électrogène' },
          { icon: 'ri-shield-check-line', label: 'Sécurité 24/7' },
          { icon: 'ri-customer-service-2-line', label: 'Accueil' },
        ],
        spaces: [
          {
            id: 'openspace',
            icon: 'ri-macbook-line',
            title: 'Open space',
            meta: '39 postes · lun – ven, 8h – 19h',
          },
          {
            id: 'private-office',
            icon: 'ri-door-closed-line',
            title: 'Bureaux privés',
            meta: 'De 6 à 25 postes · 6h – 22h, 7j/7',
          },
          {
            id: 'equipped',
            icon: 'ri-armchair-line',
            title: "Prêts à l'emploi",
            meta: 'Mobilier, internet et climatisation inclus',
          },
        ],
      },
      testimonials: {
        eyebrow: 'Témoignages',
        title: 'Ils travaillent avec nous',
        subtitle: 'Des partenaires satisfaits à travers l’Afrique de l’Ouest et centrale.',
        readMore: 'Voir plus',
        readLess: 'Voir moins',
        items: [
          {
            quote:
              "Je prends goût à gozem. Depuis que j'ai intégré l'application en tant que marchand, nous avons constaté un flux important de nouvelles commandes. Nous allons nous réorganiser afin d'en bénéficier d'avantage. Je regrette de n'avoir pas avoir commencé à travailler avec eux plus tôt.",
            author: 'Saveurs du Benin',
            role: 'Marchand sur Gozem Food au Benin depuis 2022',
          },
          {
            quote:
              'Nous avons été agréablement surpris par la qualité du service de gestion de flotte, que Gozem a mise à notre disposition. Depuis que nous avons commencé à travailler sur cette plateforme, nous avons des résultats satisfaisants.',
            author: 'STLS',
            role: 'Fleet Manager au Togo depuis 2020',
          },
          {
            quote:
              "C'est vraiment un plaisir de collaborer avec Gozem. Au-delà du fait que nos équipes aient accès à un service de transport fiable pour leurs différents rendez-vous, ce service représente pour nous un gain précieux de temps et de capitaux comparé à l'acquisition et l'entretien d'une flotte véhicules.",
            author: 'SFE Gabon',
            role: 'Client Mobilité Entreprise au Gabon depuis 2022',
          },
          {
            quote:
              'Le service coursier de Gozem est tout simplement génial. Grâce à leur flotte de coursiers professionnels et rapides, nous sommes rassurés quant à la gestion de la livraison de nos produits agroalimentaires.',
            author: 'Gajo Livestock',
            role: 'Client Gozem Coursier au Cameroun depuis 2022',
          },
          {
            quote:
              "Le partenariat signé avec Gozem a permis à Moov Africa Togo d'assurer les déplacements en voiture à Lomé de ses collaborateurs et de renforcer son efficacité opérationnelle, surtout pour les navettes de nuit.",
            author: 'Moov Africa Togo',
            role: 'Client Mobilité Entreprise au Togo depuis 2022',
          },
          {
            quote:
              "Nous utilisons depuis quelques mois le service Entreprise de gozem pour le déplacement de nos collaborateurs. Le système est flexible et les collaborateurs de notre entreprise se sont très vite adaptés. Chaque collaborateur peut commander un gozem à partir de son smartphone et régler sa course depuis le portefeuille de l'entreprise. Nous apprécions bien le système et nous profitons également pour remercier l'équipe. - Clément DAH HINKPON",
            author: 'Corporation & Millénium Popo Beach',
            role: 'Client Mobilité Entreprise au Benin depuis 2022',
          },
        ],
      },
      contact: {
        title: 'Vous avez une question ?',
        subtitle: 'Notre équipe est là pour vous accompagner à chaque étape.',
        cta: 'Écrivez-nous',
      },
      footer: {
        tagline: "La Super App N°1 d'Afrique — mobilité, livraison, commerce et paiements, réunis.",
        rights: 'Tous droits réservés.',
      },
    },
  },
  en: {
    auth: {
      login: {
        title: 'Welcome!',
        subtitle: 'Connect to your account',
        email: 'Email',
        emailPlaceholder: 'Enter email address',
        password: 'Password',
        passwordPlaceholder: 'Password',
        forgotPassword: 'Forgot password?',
        submitButton: 'Login',
        contactSupport: 'Contact support',
        noAccount: 'No account?',
        heroTitle: 'Grow your business with ease using Gozem',
        heroSubtitle:
          'Gozem Corporate provides you with a complete digital environment to automate your workflows and improve your performance',
      },
      forgotPassword: {
        title: 'Email address verification',
        subtitle: 'Enter your email address to continue',
        email: 'Email',
        emailPlaceholder: 'Enter email address',
        submitButton: 'Continue',
        backToLogin: 'Back to login',
        successMessage: 'A reset email has been sent. Check your inbox.',
      },
      selectAccount: {
        title: 'Select an account',
        subtitle: 'Which account do you want to connect to?',
        noAccounts: 'No accounts available',
        confirmTitle: 'Confirm your account',
        confirmSubtitle: 'Continue with the account below.',
        switchDisabledHint: 'To change account, use the account switcher once signed in.',
        sessionFailed: 'Could not sign in to this account',
        detailsFailed: 'Could not load the account details',
        accountUnavailable: 'This account is no longer available. Choose another one.',
        noneAvailable: 'None of your accounts are available. Contact support.',
        backToLogin: 'Back to sign in',
      },
      errors: {
        required: 'This field is required',
        invalidEmail: 'Invalid email address',
        minLength: 'Minimum {{min}} characters required',
        loginFailed: 'Incorrect email or password',
        emailNotFound: 'This email is not registered',
        invalidPassword: 'Incorrect password',
        attemptsRemainingOne: '{{count}} attempt remaining.',
        attemptsRemainingMany: '{{count}} attempts remaining.',
        networkError: 'Connection error. Please try again.',
        formInvalid: 'Enter your credentials to continue',
        accountLocked: 'Account locked due to too many failed attempts',
      },
    },
    common: {
      loading: 'Loading...',
      language: 'En',
    },
    settings: {
      recoverPin: {
        entry: {
          title: 'Recover my security code',
          subtitle: 'Reset the PIN used for your transactions',
        },
        steps: {
          password: 'Password',
          verification: 'Verification',
          newCode: 'New code',
          progressLabel: 'Progress',
        },
        titles: {
          root: 'Recover my security code',
          password: 'Enter your password',
          channel: 'How should we send your code?',
          otp: 'Enter the code we sent',
          newPin: 'Choose a new code',
          success: 'Done',
          discard: 'Discard?',
        },
        password: {
          lead: 'For your security, enter your account password before resetting your code.',
          label: 'Password',
          placeholder: 'Your password',
          cta: 'Continue',
          ctaLoading: 'Checking…',
          wrong: 'Incorrect password. Check your entry and try again.',
          wrongRemainingOne: 'Incorrect password. You have {{count}} attempt left.',
          wrongRemainingMany: 'Incorrect password. You have {{count}} attempts left.',
          show: 'Show password',
          hide: 'Hide password',
        },
        channel: {
          lead: "We'll send a 4-digit code to confirm it's you.",
          groupLabel: 'Verification method',
          cta: 'Send the code',
          ctaSending: 'Sending…',
          sms: 'SMS',
          whatsapp: 'WhatsApp',
          email: 'Email',
          none: 'No verification method is available on your account. Please contact support.',
          sendFailed: "We couldn't send the code. Check your connection and try again.",
        },
        otp: {
          lead: 'Code sent {{channel}}. It stays valid for {{expiry}}.',
          bySms: 'by SMS',
          byWhatsapp: 'on WhatsApp',
          byEmail: 'by email',
          cta: 'Continue',
          resend: 'Resend the code',
          resendIn: 'Resend the code in {{time}}',
          resent: 'A new code has just been sent to you.',
          invalid: 'That code is not valid or has expired. Enter it again, or request a new one.',
          resendFailed: "We couldn't resend the code. Check your connection and try again.",
          defaultExpiry: '30 minutes',
        },
        pin: {
          lead: 'This 4-digit code will protect your transactions.',
          cta: 'Save the code',
          ctaSaving: 'Saving…',
          encryptionFailed: "We couldn't secure your code. Try again in a moment.",
        },
        success: {
          title: 'Security code updated',
          description: 'Use your new code to approve your next transactions.',
          cta: 'Done',
        },
        blocked: {
          title: 'Too many attempts',
          description:
            'To protect your account, resetting is temporarily blocked. Try again later or contact support.',
          cta: 'Close',
        },
        network: {
          title: 'Connection lost',
          description:
            'Your code was not changed. Check your connection and try again — your current code still works.',
          retry: 'Try again',
          close: 'Close',
        },
        session: {
          title: 'Session expired',
          description: 'Sign in again to reset your security code.',
          cta: 'Close',
        },
        discard: {
          title: 'Discard the reset?',
          description:
            "The verification code already sent will be cancelled and you'll have to start over.",
          confirm: 'Discard',
          cancel: 'Continue resetting',
        },
        stepAnnouncement: 'Step {{index}} of 3: {{name}}',
        close: 'Close',
        genericError: 'An error occurred. Please try again.',
      },
    },
    landing: {
      nav: {
        partners: 'Partners',
        benefits: 'Benefits',
        testimonials: 'Testimonials',
        contact: 'Contact',
        login: 'Login',
        myAccount: 'My account',
      },
      hero: {
        badge: "Africa's N°1 Super App",
        title: 'Become a Gozem Partner',
        subtitle: "Join Africa's N°1 Super App like 12,000 other partners.",
        ctaPrimary: 'Join Gozem',
        ctaPartner: 'Become a Partner',
        ctaSecondary: 'See partner stories',
        statValue: '12,000+',
        statLabel: 'partners trust us',
        stats: [
          { value: '12,000+', label: 'partners' },
          { value: '5', label: 'countries' },
          { value: '5', label: 'partner profiles' },
        ],
      },
      joinForm: {
        title: 'Join Us',
        subtitle: 'Become a partner in a few steps.',
        partnerTypeLabel: 'Partner Type',
        partnerTypes: ['Online Seller', 'Mobility Pro', 'Courier', 'Fleet', 'Freelance'],
        partnerTypeDescriptions: [
          'Take your orders in the Merchant app — our drivers handle the delivery.',
          "One company account to book and pay for your employees' trips.",
          'Get your documents and parcels delivered the same day, tracked in real time.',
          'Put your vehicles to work with drivers already trained by Gozem.',
          'Refer Gozem and earn a commission on what your referrals do.',
        ],
        companyNamePlaceholder: 'Company Name',
        firstNamePlaceholder: 'First Name',
        lastNamePlaceholder: 'Last Name',
        emailPlaceholder: 'Email Address',
        agentNamePlaceholder: 'Agent Name',
        agentCodePlaceholder: 'Agent Referral Code',
        requiredError: 'This field is required.',
        emailError: 'Please enter a valid email address.',
        phoneLabel: 'Phone number',
        phonePlaceholder: 'Phone number',
        consent: 'I agree to receive messages on WhatsApp / via SMS',
        howLabel: 'How did you know us?',
        howOptions: [
          'Gozem Agent',
          'Facebook',
          'Instagram',
          'Google',
          'Twitter',
          'Linkedin',
          'Tik Tok',
          'SMS',
          'Email',
          'Panneau Publicitaire',
          'TV',
          'Radio',
          'Notification',
          'Website',
          'Other',
        ],
        submit: 'Send',
        success: {
          title: 'Sign Up Complete',
          body: "Welcome to Gozem! We will now be processing your application. We will notify you once we're done processing your application.",
          close: 'Close',
        },
        errors: {
          title: 'Error',
          fillAllFields: 'Please fill in all fields.',
          phoneAlreadyRegistered: 'This phone number is already registered.',
          emailAlreadyRegistered: 'This email address is already registered.',
          generic: 'Something went wrong. Please try again.',
        },
      },
      marquee: {
        label: 'Trusted across West and Central Africa',
      },
      partners: {
        eyebrow: 'Five ways to partner',
        title: 'Grow your business with Gozem',
        subtitle:
          'Five profiles, one platform. Pick yours and see exactly what Gozem changes for your business.',
        reasonsLabel: 'Is this you?',
        benefitsLabel: 'What you get',
        advisorCta: 'Talk to an advisor',
        types: [
          {
            id: 'merchant',
            label: 'Online Seller',
            tag: 'Sell more',
            icon: 'ri-store-2-line',
            headline: 'Sell more, we handle the delivery',
            intro:
              'List your business on Gozem, take orders in the Merchant app and let our fleet of drivers handle the rest.',
            reasons: [
              {
                icon: 'ri-restaurant-2-line',
                text: 'You run a restaurant or a local eatery with regular customers',
              },
              { icon: 'ri-shopping-bag-3-line', text: 'You sell local products online' },
              {
                icon: 'ri-megaphone-line',
                text: 'You want to be known beyond your own neighbourhood',
              },
            ],
            benefits: [
              {
                title: 'Delivery that grows your sales',
                description:
                  'Receive and process your orders in the Merchant app. Our drivers take over, so you stay focused on production.',
              },
              {
                title: 'Coaching that never stops',
                description:
                  'A dedicated coach tracks your performance and trains you regularly: preparation times, service quality, customer ratings.',
              },
              {
                title: 'Marketing included',
                description:
                  "Join Gozem's promotional campaigns and gain visibility in the app and on our social channels.",
              },
            ],
          },
          {
            id: 'mobility',
            label: 'Mobility Pro',
            tag: 'Move your teams',
            icon: 'ri-briefcase-4-line',
            headline: "Take control of your teams' travel",
            intro:
              'One company account to book, control and pay for every trip your employees take, with no out-of-pocket expenses.',
            reasons: [
              { icon: 'ri-team-line', text: 'Your teams travel every day for your operations' },
              { icon: 'ri-moon-line', text: 'Your employees finish late or start very early' },
              { icon: 'ri-group-line', text: 'You have no company vehicles to hand out' },
            ],
            benefits: [
              {
                title: 'Clear rules for every team',
                description:
                  'Create user groups and set their usage rules: budgets, hours, allowed services. No more case-by-case approvals.',
              },
              {
                title: 'Reports ready to use',
                description:
                  'Track spending per employee, per department or per period, and export your reports for accounting.',
              },
              {
                title: 'A single account to settle',
                description:
                  'Every trip is paid from the company account, with centralized, traceable invoicing. No more expense claims.',
              },
            ],
          },
          {
            id: 'courier',
            label: 'Courier',
            tag: 'Deliver your parcels',
            icon: 'ri-e-bike-2-line',
            headline: 'Get your parcels delivered the same day',
            intro:
              'Hand your documents and parcels to professional couriers, tracked in real time from pickup to hand-off.',
            reasons: [
              { icon: 'ri-mail-send-line', text: 'You regularly send documents to your customers' },
              {
                icon: 'ri-shopping-cart-2-line',
                text: 'You run an online store that needs delivery',
              },
              {
                icon: 'ri-restaurant-line',
                text: 'You are a restaurant owner delivering your dishes',
              },
            ],
            benefits: [
              {
                title: 'A courier in three clicks',
                description:
                  'Order on demand and group several parcels or several stops into one trip, from the same interface.',
              },
              {
                title: 'Shipments secured by code',
                description:
                  'Generate a PIN when you order: the parcel is only handed to whoever holds that code. Your shipments are covered end to end.',
              },
              {
                title: 'You choose who pays',
                description:
                  'Have the delivery paid by the sender or by the recipient, whichever suits your business.',
              },
            ],
          },
          {
            id: 'fleet',
            label: 'Fleet',
            tag: 'Monetize your vehicles',
            icon: 'ri-taxi-line',
            headline: 'Make your fleet profitable, without managing it alone',
            intro:
              'Put your vehicles to work on Gozem with drivers who are already trained, and full visibility on your activity.',
            reasons: [
              { icon: 'ri-car-line', text: 'You own a fleet of rental vehicles' },
              {
                icon: 'ri-roadster-line',
                text: 'You are an individual with several cars or motorbikes',
              },
              {
                icon: 'ri-building-line',
                text: 'You are looking for a profitable, closely tracked investment',
              },
            ],
            benefits: [
              {
                title: 'Drivers already trained',
                description:
                  'We connect you with our Champions: partner drivers who are selected, vetted and trained on the app.',
              },
              {
                title: 'Your activity in real time',
                description:
                  'Track the position and performance of every Champion from your platform. Data is raw and exportable: your analysis stays yours.',
              },
              {
                title: 'Cash flow under control',
                description:
                  'Your earnings are collected in your e-wallet and paid out by bank transfer, at the pace you choose.',
              },
            ],
          },
          {
            id: 'freelance',
            label: 'Freelance',
            tag: 'Earn by referring',
            icon: 'ri-user-star-line',
            headline: 'Earn money by talking about Gozem',
            intro:
              'Introduce Gozem to the people around you and earn a commission on what each of your referrals does.',
            reasons: [
              {
                icon: 'ri-graduation-cap-line',
                text: 'You are a student, comfortable with digital tools',
              },
              { icon: 'ri-share-forward-line', text: 'You like sharing your good tips' },
              { icon: 'ri-medal-line', text: 'You are a recent graduate looking for extra income' },
            ],
            benefits: [
              {
                title: 'Simply refer',
                description:
                  'Install Gozem for your family, your neighbours, your colleagues. Every validated sign-up is recorded on your freelance account.',
              },
              {
                title: 'Income that adds up',
                description:
                  'You earn a commission as soon as one of your referrals books a ride, a meal or a delivery in the app.',
              },
              {
                title: 'No equipment, no schedule',
                description:
                  'A smartphone is enough. You work where you want, when you want, with no imposed targets.',
              },
            ],
          },
        ],
      },
      coworking: {
        eyebrow: 'New in Togo',
        title: 'Gozem Coworking',
        lead: 'A modern setting to work, host and move forward. In Lomé, Gozem Coworking brings together open space, private offices, a meeting room, common areas and a rooftop.',
        location: 'Nyékonakpoé, Lomé — Gozem Togo head office',
        navLabel: 'Coworking',
        ctaVisit: 'Request a visit',
        ctaSpaces: 'View the spaces',
        amenities: [
          { icon: 'ri-wifi-line', label: 'High-speed internet' },
          { icon: 'ri-restaurant-2-line', label: 'Fully equipped kitchen' },
          { icon: 'ri-sofa-line', label: 'Lounge area' },
          { icon: 'ri-snowy-line', label: 'Air conditioning' },
          { icon: 'ri-flashlight-line', label: 'Backup generator' },
          { icon: 'ri-shield-check-line', label: '24/7 security' },
          { icon: 'ri-customer-service-2-line', label: 'Reception' },
        ],
        spaces: [
          {
            id: 'openspace',
            icon: 'ri-macbook-line',
            title: 'Open space',
            meta: '39 desks · Mon – Fri, 8am – 7pm',
          },
          {
            id: 'private-office',
            icon: 'ri-door-closed-line',
            title: 'Private offices',
            meta: '6 to 25 desks · 6am – 10pm, 7 days',
          },
          {
            id: 'equipped',
            icon: 'ri-armchair-line',
            title: 'Move-in ready',
            meta: 'Furniture, internet and air conditioning',
          },
        ],
      },
      testimonials: {
        eyebrow: 'Partner stories',
        title: 'They work with us',
        subtitle: 'Satisfied partners across West and Central Africa.',
        readMore: 'Read more',
        readLess: 'Read less',
        items: [
          {
            quote:
              'I am getting a taste for gozem. Since I joined the app as a merchant, we have seen a significant flow of new orders. We are going to reorganize ourselves in order to benefit from it more. I regret not having started working with them sooner.',
            author: 'Saveurs du Benin',
            role: 'Merchant on Gozem Food in Benin since 2022',
          },
          {
            quote:
              'We have been pleasantly surprised by the quality of the fleet management service, which Gozem has made available to us. Since we started working on this platform, we have satisfactory results.',
            author: 'STLS',
            role: 'Fleet Manager in Togo since 2020',
          },
          {
            quote:
              'It is truly a pleasure to work with Gozem. Beyond the fact that our teams have access to a reliable transportation service for their various appointments, this service represents for us a precious saving of time and capital compared to the acquisition and maintenance of a fleet of vehicles.',
            author: 'SFE Gabon',
            role: 'Corporate Mobility Client in Gabon since 2022',
          },
          {
            quote:
              "Gozem's courier service is simply brilliant. With their fleet of professional and fast couriers, we are reassured about the management of the delivery of our agri-food products.",
            author: 'Gajo Livestock',
            role: 'Gozem Courier Client in Cameroon since 2022',
          },
          {
            quote:
              'The partnership signed with Gozem has enabled Moov Africa Togo to provide car travel in Lomé for its employees and increase its operational efficiency, especially for night shuttles.',
            author: 'Moov Africa Togo',
            role: 'Corporate Mobility Client in Togo since 2022',
          },
          {
            quote:
              "We have been using gozem's Enterprise service for a few months now for the movement of our employees. The system is flexible and the employees of our company have adapted very quickly. Each employee can order a gozem from his or her smartphone and pay for the trip from the company wallet. We really appreciate the system and we also take the opportunity to thank the team. - Clément DAH HINKPON",
            author: 'Corporation & Millénium Popo Beach',
            role: 'Corporate Mobility Client in Benin since 2022',
          },
        ],
      },
      contact: {
        title: 'Do you have a question?',
        subtitle: 'Our team is here to support you every step of the way.',
        cta: 'Contact Us',
      },
      footer: {
        tagline: "Africa's N°1 Super App — mobility, delivery, commerce and payments, all in one.",
        rights: 'All rights reserved.',
      },
    },
  },
};
